import React, { useContext } from "react";
import { Box, Grid, Typography } from "@mui/material";
import Image from "mui-image";

import useTruncateString from "hooks/useTruncateString";

import { UserContext } from "utils/UserContext";
import handleConnectionLevel from "utils/connectionUtils";
import {
  handleCapsMarginBottom,
  handleCapsVariant,
  handleScorePadding,
  handleScorePaddingBottom,
  handleScoreVariant,
  handleScoreWidth,
} from "utils/customStylesUtils";
import PropTypes from "prop-types";

function ImgDoubleCols({ displayOnly, widthBelowSm }) {
  const { selectedAlbum, connectionScore } = useContext(UserContext);

  const handleTruncatedString = useTruncateString();

  const connectionLevel = handleConnectionLevel(connectionScore);

  const badgeStyle = !displayOnly
    ? {
        flex: 1,
        objectFit: "cover",
        width: "7rem",
        height: "3rem",
        maxWidth: "7rem",
        minWidth: "7rem",
      }
    : {
        flex: 1,
        objectFit: "cover",
        width: "100%",
      };

  const titleStyle = {
    fontWeight: "400",
    textTransform: "uppercase",
    fontSize: widthBelowSm && displayOnly && "0.65rem",
    marginBottom: handleCapsMarginBottom(displayOnly, widthBelowSm),
  };

  return (
    <Grid
      container
      item
      xs={4}
      direction="row"
      justifyContent="center"
      alignItems="center"
      spacing={4}
      sx={{
        flexWrap: "nowrap",
      }}
    >
      <Grid
        item
        xs={6}
        sx={{
          textAlign: "center",
        }}
      >
        <Image
          src={selectedAlbum.img}
          alt={selectedAlbum.name}
          duration={0}
          sx={{
            width: "100%",
          }}
        />
      </Grid>

      <Grid
        container
        direction="column"
        item
        spacing={!displayOnly ? 4 : 1}
        xs={6}
        sx={{
          textAlign: "center",
        }}
      >
        <Grid item>
          {!displayOnly ? (
            <Typography
              variant="h5"
              component="p"
              sx={{
                fontWeight: 600,
                fontStyle: "italic",
              }}
            >
              {selectedAlbum.name}
            </Typography>
          ) : (
            handleTruncatedString({
              customText: selectedAlbum.name,
              customVariant: widthBelowSm ? "body2" : "body1",
              customComponent: "p",
              customSx: {
                fontWeight: 600,
                fontStyle: "italic",
              },
              maxCharacters: {
                sm: widthBelowSm ? 8 : 15,
                smToMd: 15,
                aboveMd: 15,
              },
            })
          )}
          {!displayOnly ? (
            <Typography
              variant="h5"
              component="p"
              sx={{
                fontWeight: 600,
              }}
            >
              {selectedAlbum.mainArtist}
            </Typography>
          ) : (
            handleTruncatedString({
              customText: selectedAlbum.mainArtist,
              customVariant: widthBelowSm ? "caption" : "body2",
              customComponent: "p",
              customSx: {
                fontWeight: 600,
                fontStyle: "italic",
              },
              maxCharacters: {
                sm: widthBelowSm ? 8 : 14,
                smToMd: 14,
                aboveMd: 14,
              },
            })
          )}
        </Grid>

        <Grid item>
          <Typography
            component="p"
            variant={handleCapsVariant(displayOnly, widthBelowSm)}
            sx={titleStyle}
          >
            Connection Level
          </Typography>
          <Box
            sx={
              displayOnly
                ? {
                    display: "flex",
                    width: widthBelowSm ? "3rem" : "4rem",
                    margin: "auto",
                  }
                : {}
            }
          >
            <Image
              src={connectionLevel.imagePath}
              alt=""
              duration={0}
              sx={badgeStyle}
            />
          </Box>
        </Grid>

        <Grid item>
          <Typography
            component="p"
            variant={handleCapsVariant(displayOnly, widthBelowSm)}
            sx={titleStyle}
          >
            Connection Score
          </Typography>
          <Box
            sx={{
              width: handleScoreWidth(displayOnly, widthBelowSm),
              padding: handleScorePadding(displayOnly, widthBelowSm),
              paddingBottom: handleScorePaddingBottom(
                displayOnly,
                widthBelowSm
              ),
              margin: "auto",
              borderRadius: "0.3rem",
              fontWeight: "bold",
              backgroundColor: connectionLevel.color,
            }}
          >
            <Typography
              variant={handleScoreVariant(displayOnly, widthBelowSm)}
              component="span"
            >
              {connectionScore}
            </Typography>
          </Box>
        </Grid>
      </Grid>
    </Grid>
  );
}

ImgDoubleCols.propTypes = {
  displayOnly: PropTypes.bool.isRequired,
  widthBelowSm: PropTypes.bool.isRequired,
};

export default ImgDoubleCols;
